// REQ-P1-06b: 管理者支払/請求参照の契約型付きAPI。

import { api, type paths } from './client';
import type { operations } from './types.gen';
import { ApiStatusError } from './templates';

type MonthlyPaymentsQuery = NonNullable<
  paths['/admin/payments/monthly']['get']['parameters']['query']
>;
type MonthlyPaymentsExtendedQuery = MonthlyPaymentsQuery & {
  assigned_to_me?: string | boolean;
};
type MonthlyPaymentsResponse = NonNullable<
  paths['/admin/payments/monthly']['get']['responses'][200]['content']
>['application/json'];
type BatchDownloadAdminInvoicesRequest = NonNullable<
  paths['/admin/invoices/batch-download']['post']['requestBody']
>['content']['application/json'];
type AdminInvoiceDownloadLogsResponse = NonNullable<
  paths['/admin/invoices/{contractArtistId}/download-logs']['get']['responses'][200]['content']
>['application/json'];
type InvoiceStorageDebugResponse = NonNullable<
  paths['/contracts/{contractArtistId}/invoice/debug']['get']['responses'][200]['content']
>['application/json'];
type InvoicePdfFileQuery = NonNullable<
  paths['/contracts/{contractArtistId}/invoice/file']['get']['parameters']['query']
>;
type AdminFinanceYearsResponse = NonNullable<
  operations['listAdminFinanceYears']['responses'][200]['content']
>['application/json'];
type AdminFinanceDetailsQuery = NonNullable<
  operations['listAdminFinanceDetails']['parameters']['query']
>;
type AdminFinanceDetailsResponse = NonNullable<
  operations['listAdminFinanceDetails']['responses'][200]['content']
>['application/json'];
type RefreshAdminPlAutoEntriesRequest = NonNullable<
  operations['refreshAdminPlAutoEntries']['requestBody']
>['content']['application/json'];
type RefreshAdminPlAutoEntriesResponse = NonNullable<
  operations['refreshAdminPlAutoEntries']['responses'][200]['content']
>['application/json'];
type ArtistRewardYearsResponse = NonNullable<
  operations['listArtistRewardYears']['responses'][200]['content']
>['application/json'];
type ArtistRewardsQuery = NonNullable<
  operations['listArtistRewards']['parameters']['query']
>;
type ArtistRewardsResponse = NonNullable<
  operations['listArtistRewards']['responses'][200]['content']
>['application/json'];
type LegacyPaymentJsonRequestOptions = {
  method?: string;
  headers?: Record<string, string>;
  body?: XMLHttpRequestBodyInit | null;
};

const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

function getApiErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      if (parsed && typeof parsed === 'object') {
        const record = parsed as Record<string, unknown>;
        const message = record.message || record.error;
        return typeof message === 'string' ? message : error;
      }
    } catch {
      return error;
    }
  }
  if (typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const message = record.message || record.error;
  return typeof message === 'string' ? message : undefined;
}

function throwApiError(status: number, error: unknown, fallbackMessage: string): never {
  const statusError = new ApiStatusError(status);
  statusError.message = getApiErrorMessage(error) || fallbackMessage;
  throw statusError;
}

function requestLegacyPaymentJson(
  path: string,
  options: LegacyPaymentJsonRequestOptions = {}
): Promise<Record<string, unknown>> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(String(options.method || 'GET').toUpperCase(), `${API_ORIGIN}${path}`);
    request.withCredentials = true;
    request.setRequestHeader('Accept', 'application/json');
    Object.entries(options.headers || {}).forEach(([key, value]) => {
      if (value != null) request.setRequestHeader(key, String(value));
    });

    request.onload = () => {
      let payload: Record<string, unknown> = {};
      try {
        payload = request.responseText
          ? (JSON.parse(request.responseText) as Record<string, unknown>)
          : {};
      } catch (error) {
        reject(error);
        return;
      }

      if (request.status < 200 || request.status >= 300 || payload.success === false) {
        reject(
          new Error(
            (typeof payload.message === 'string' && payload.message) ||
              (typeof payload.error === 'string' && payload.error) ||
              `HTTP ${request.status}`
          )
        );
        return;
      }
      resolve(payload);
    };

    request.onerror = () => reject(new Error('Network request failed'));
    request.onabort = () => reject(new Error('Network request aborted'));
    request.ontimeout = () => reject(new Error('Network request timed out'));
    request.send(options.body || null);
  });
}

export async function getLegacyPaymentInvoice(
  contractArtistId: number | string
): Promise<Record<string, unknown>> {
  const payload = await requestLegacyPaymentJson(
    `/api/admin/payments/contract-artist/${encodeURIComponent(String(contractArtistId))}/invoice`
  );
  const data = payload.data;
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
}

export async function updateLegacyPaymentInvoiceDownloaded(
  contractArtistId: number | string,
  invoiceDownloaded: boolean
): Promise<Record<string, unknown>> {
  return requestLegacyPaymentJson(
    `/api/admin/payments/contract-artist/${encodeURIComponent(String(contractArtistId))}/invoice-downloaded`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoiceDownloaded }),
    }
  );
}

export async function listAdminMonthlyPayments(
  query: MonthlyPaymentsExtendedQuery
): Promise<MonthlyPaymentsResponse> {
  const { data, error, response } = await api.GET('/admin/payments/monthly', {
    params: { query },
  });
  if (!response.ok || !data) throwApiError(response.status, error, '取得に失敗しました');
  return data as MonthlyPaymentsResponse;
}

export async function listAdminInvoiceDownloadLogs(
  contractArtistId: number | string
): Promise<AdminInvoiceDownloadLogsResponse> {
  const { data, error, response } = await api.GET('/admin/invoices/{contractArtistId}/download-logs', {
    params: { path: { contractArtistId: String(contractArtistId) } },
  });
  if (!response.ok || !data) throwApiError(response.status, error, '履歴の取得に失敗しました');
  return data as AdminInvoiceDownloadLogsResponse;
}

export async function getInvoiceStorageDebug(
  contractArtistId: number | string
): Promise<InvoiceStorageDebugResponse> {
  const { data, error, response } = await api.GET('/contracts/{contractArtistId}/invoice/debug', {
    params: { path: { contractArtistId: String(contractArtistId) } },
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, '請求書PDFが未生成か、アクセスできません。');
  }
  return data as InvoiceStorageDebugResponse;
}

export async function downloadAdminInvoicePdfFile(
  contractArtistId: number | string,
  query: InvoicePdfFileQuery = {}
): Promise<Blob> {
  const { data, error, response } = await api.GET('/contracts/{contractArtistId}/invoice/file', {
    params: { path: { contractArtistId: String(contractArtistId) }, query },
    parseAs: 'blob',
  });
  if (!response.ok || !data) throwApiError(response.status, error, 'PDFの取得に失敗しました');
  return data as Blob;
}

export async function downloadAdminInvoicesZip(ids: Array<number | string>): Promise<Blob> {
  const body: BatchDownloadAdminInvoicesRequest = { ids: ids.map(String) };
  const { data, error, response } = await api.POST('/admin/invoices/batch-download', {
    body,
    parseAs: 'blob',
  });
  if (!response.ok || !data) throwApiError(response.status, error, '一括ダウンロードに失敗しました');
  return data as Blob;
}

export async function refreshAdminPlAutoEntries(
  body: RefreshAdminPlAutoEntriesRequest = {}
): Promise<RefreshAdminPlAutoEntriesResponse> {
  const { data, error, response } = await api.POST('/admin/pl/refresh-auto', { body });
  if (!response.ok || !data) throwApiError(response.status, error, 'PL自動明細の更新に失敗しました');
  return data as RefreshAdminPlAutoEntriesResponse;
}

export async function listAdminFinanceYears(
  options: { signal?: AbortSignal } = {}
): Promise<AdminFinanceYearsResponse> {
  const { data, error, response } = await api.GET('/admin/finance/years', {
    signal: options.signal,
  });
  if (!response.ok || !data) throwApiError(response.status, error, '年別サマリの取得に失敗しました');
  return data as AdminFinanceYearsResponse;
}

export async function listAdminFinanceDetails(
  query: AdminFinanceDetailsQuery = {},
  options: { signal?: AbortSignal } = {}
): Promise<AdminFinanceDetailsResponse> {
  const { data, error, response } = await api.GET('/admin/finance', {
    params: { query },
    signal: options.signal,
  });
  if (!response.ok || !data) throwApiError(response.status, error, '経理データの取得に失敗しました');
  return data as AdminFinanceDetailsResponse;
}

export async function listArtistRewardYears(
  options: { signal?: AbortSignal } = {}
): Promise<ArtistRewardYearsResponse> {
  const { data, response } = await api.GET('/artist/rewards/years', {
    signal: options.signal,
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as ArtistRewardYearsResponse;
}

export async function listArtistRewards(
  query: ArtistRewardsQuery,
  options: { signal?: AbortSignal } = {}
): Promise<ArtistRewardsResponse> {
  const { data, response } = await api.GET('/artist/rewards', {
    params: { query },
    signal: options.signal,
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as ArtistRewardsResponse;
}

export async function deleteArtistExternalInvoice(invoiceId: string): Promise<Record<string, unknown>> {
  const normalizedId = String(invoiceId || '').trim();
  if (!normalizedId) throw new Error('削除対象が不正です');
  const { data, error, response } = await (api as any).DELETE('/artist/external-invoices/{invoiceId}', {
    params: { path: { invoiceId: normalizedId } },
  });
  if (!response.ok || !data) throwApiError(response.status, error, '削除に失敗しました');
  return data as Record<string, unknown>;
}
